// The booking lens's "+ New offer" — a PRE-HOLD SHORTLIST row, not a show.
//
// An offer here is a date the agent is chasing: who is asking, on what deal, and how long the offer stands.
// Nothing about it touches the schedule spine, so it never reaches the band's calendar. ART-326 adds "What happens
// next": SAVE it as a soft hold (only the agency sees it, nothing is sent), or CONTINUE into the "Send to team" hold
// editor, which turns it into a show request for the artist's management and AV. A real hold only exists once they
// answer.
//
// That is why this is a short quick form and not the show editor with fields hidden. A shortlist is written
// from an email in ten seconds, and everything the editor asks for — city, capacity, doors, lineup, the deal's
// road costs — is unknown at this stage and would be a form full of blanks pretending to be a show.
//
// THE DEAL HAS TWO SHAPES (PO, 2026-09-02 — ART-115). A flat GUARANTEE is one number. TERMS is the industry
// "versus" deal: "$1,000 or 70% of the door, whichever is greater". Both figures are the offer, so the form
// asks for both and the board renders both — a terms offer showing only its guarantee would understate every
// deal an agent actually chases. The pair maps straight onto `show_finances.guarantee_cents` +
// `door_split_pct`, which `SettlementCalculator` already resolves as `max(guarantee, doorShare)`, so nothing
// new computes anything: converting a terms offer produces a terms show.
//
// Money rides the row rather than a parallel money list, because this whole surface is `offers:manage`, which
// is Booking-root only and therefore has exactly one audience. There is no money-free seat here to shape a
// second shape for. (See BookingOffersController for why it is deliberately NOT also money-gated.)
import { useId, useState, type FormEvent } from "react";
import { Button, Field, Modal, SegmentedControl, Textarea, DialogCancel } from "@cntrl/ui";
import {
  OFFER_DETAILS_MAX,
  useCreateBookingOffer,
  useUpdateBookingOffer,
  useDeleteBookingOffer,
  type BookingOfferDto,
  type BookingOfferRequest,
} from "../../app/hooks/useBookingOffers.js";
import { ConfirmDialog } from "../shared/ConfirmDialog.js";
import { SendToTeamModal } from "./SendToTeamModal.js";
import styles from "./NewOfferModal.module.scss";

/** Show / Festival — the wire values are `artist.shows.kind`, so conversion carries the choice straight
 *  across into the editor with no translation table between them. */
const KINDS: PillOption[] = [
  { value: "show", label: "Show" },
  { value: "festival", label: "Festival" },
];

/** Guarantee / Terms — the wire values are `artist.booking_offers.deal_type`. */
const DEALS: PillOption[] = [
  { value: "guarantee", label: "Guarantee" },
  { value: "terms", label: "Terms" },
];

/** `1234.50` ⇄ `123450`. Blank stays blank: an offer with no number yet is a real shortlist row, and a
 *  fabricated 0 would read on the board as an offer of nothing. */
function centsToInput(cents: number | null | undefined): string {
  return cents == null ? "" : (cents / 100).toFixed(2);
}
function inputToCents(v: string): number | null {
  const t = v.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}
/** The door share, as typed. Same blank-stays-blank rule: "terms, split still being argued" is a real row,
 *  and it is a different fact from a flat guarantee — which is why `dealType` is stored rather than inferred
 *  from whether this number is present. */
function pctToInput(pct: number | null | undefined): string {
  return pct == null ? "" : String(pct);
}
function inputToPct(v: string): number | null {
  const t = v.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

interface PillOption {
  value: string;
  label: string;
}

/**
 * The two-pill toggle — the same control 01 Basics uses for the show kind, so the choice made here and the
 * one the editor shows after conversion are visibly the same control rather than two designs for one fact.
 *
 * Local, and a component rather than two copies of the markup: this form now has two of them, and a fieldset
 * + legend + `aria-pressed` group is exactly the shape that drifts when it is written twice. A segmented
 * toggle is on the COMPONENTS.md extraction roadmap; this is the second and third instance in the app.
 */
function PillToggle({
  legend, options, value, onChange,
}: {
  legend: string;
  options: PillOption[];
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <fieldset className={styles.pillField}>
      <legend className={styles.pillLegend}>{legend}</legend>
      {/* Full-width, split evenly — the offer form's two toggles are deliberately a two-halves bar, not the
          content-width default (this is the `stretch` case the primitive exists to make explicit). */}
      <SegmentedControl label={legend} value={value} onChange={onChange} options={options} stretch />
    </fieldset>
  );
}

export interface NewOfferModalProps {
  onClose: () => void;
  /** Pass an existing offer to EDIT it (a full-replace PUT); omit to create. */
  offer?: BookingOfferDto;
}

export function NewOfferModal({ offer, onClose }: NewOfferModalProps) {
  const create = useCreateBookingOffer();
  const update = useUpdateBookingOffer();
  const destroy = useDeleteBookingOffer();
  const editing = !!offer;

  const [date, setDate] = useState(offer?.date ?? "");
  const [buyerVenue, setBuyerVenue] = useState(offer?.buyerVenue ?? "");
  const [dealType, setDealType] = useState(offer?.dealType === "terms" ? "terms" : "guarantee");
  const [amount, setAmount] = useState(centsToInput(offer?.amountCents));
  const [doorSplitPct, setDoorSplitPct] = useState(pctToInput(offer?.doorSplitPct));
  const [kind, setKind] = useState(offer?.kind === "festival" ? "festival" : "show");
  const [goodThrough, setGoodThrough] = useState(offer?.goodThrough ?? "");
  const [details, setDetails] = useState(offer?.details ?? "");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // ART-326 — "What happens next": a soft hold stays with the agency; "Send to team" continues into the hold editor.
  const requested = offer?.status === "requested";
  const passed = offer?.status === "passed";
  const [next, setNext] = useState<"soft" | "team">("soft");
  // The saved offer the hold editor opens on (after Continue, or "Edit request" on a request already sent).
  const [sendFor, setSendFor] = useState<BookingOfferDto | null>(null);
  // The terms rule describes the PAIR, not either box, so it is one line under the row rather than a `hint`
  // on the right-hand field — which is where it started, and which both indented it under the door split and
  // read as a note about that number alone. It stays wired to BOTH inputs so a screen reader still hears it.
  const termsHintId = useId();
  const nextId = useId();

  const terms = dealType === "terms";
  const busy = create.isPending || update.isPending;
  // The date is the only requirement. Everything else arrives as the conversation does — which is the point of
  // a shortlist: a row you can write the moment a date is claimed, before there is a number or even a room.
  const canSubmit = date.trim() !== "" && !busy;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const body: BookingOfferRequest = {
      date,
      buyerVenue: buyerVenue.trim() === "" ? null : buyerVenue.trim(),
      amountCents: inputToCents(amount),
      dealType,
      // Sent only on a terms offer. The server drops it either way on a guarantee (and a CHECK constraint
      // refuses the pairing at the floor), but sending the hidden field's stale draft would be asking the
      // server to ignore us rather than simply not saying it.
      doorSplitPct: terms ? inputToPct(doorSplitPct) : null,
      kind,
      // Blank CLEARS rather than sending an empty string: "no stated deadline" is a real state the board
      // renders differently from a deadline that has passed.
      goodThrough: goodThrough.trim() === "" ? null : goodThrough,
      // Same blank-clears rule, and here it is what decides whether the card offers "View details" at all: a
      // verb whose only outcome is an empty box is the dead button this app refuses elsewhere.
      details: details.trim() === "" ? null : details.trim(),
    };
    // Continue → save the offer as it stands, then open the hold editor on it. Anything else just saves.
    const after = (saved: BookingOfferDto) => (next === "team" && !requested ? setSendFor(saved) : onClose());
    if (offer?.offerId) {
      update.mutate({ offerId: offer.offerId, body }, { onSuccess: after });
      return;
    }
    create.mutate(body, { onSuccess: after });
  }

  if (sendFor) return <SendToTeamModal offer={sendFor} onClose={onClose} />;

  return (
    <>
      <Modal
        className={styles.modal}
        title={editing ? "Edit offer" : "New offer"}
        size="md"
        onClose={onClose}
        bodyAs="form"
        onSubmit={submit}
        footer={
          <>
            {/* A request waiting on the team cannot be deleted from here — it is the team's to answer. */}
            {editing && !requested && (
              <Button
                type="button"
                variant="ghost"
                className={styles.footerDanger}
                onClick={() => setConfirmingDelete(true)}
              >
                Delete
              </Button>
            )}
            {requested && (
              <Button type="button" variant="ghost" onClick={() => setSendFor(offer!)}>
                Edit request
              </Button>
            )}
            <DialogCancel />
            <Button type="submit" disabled={!canSubmit} loading={busy}>
              {requested || passed ? "Save changes" : next === "team" ? "Continue" : "Save soft hold"}
            </Button>
          </>
        }
      >
        <Field
          label="Date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          required
        />

        <Field
          // ART-330 — the BUYER. The venue is picked in Send to team, from the show editor's own venue search, and
          // the buyer named here carries over as the promoter / buyer there.
          label="Buyer"
          type="text"
          value={buyerVenue}
          onChange={(e) => setBuyerVenue(e.target.value)}
          placeholder="e.g. Live Nation"
          maxLength={200}
        />

        <PillToggle legend="Deal" options={DEALS} value={dealType} onChange={setDealType} />

        {/* `$` sits INSIDE the box (the `affixStart` contract), so the figure reads as one value.
            On TERMS the two figures are ONE deal, so they pair across a row with the rule spelled out under
            BOTH of them — the exception this file's stylesheet notes, and the reason the label changes with
            the mode: "Amount (offer)" over a number that is only the floor of a versus deal is the same
            figure wearing the wrong name. */}
        {terms ? (
          <div className={styles.terms}>
            <div className={styles.termsRow}>
              <Field
                label="Guarantee"
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                affixStart="$"
                aria-describedby={termsHintId}
              />
              <Field
                label="Door split"
                type="number"
                inputMode="decimal"
                min={0}
                max={100}
                step="1"
                value={doorSplitPct}
                onChange={(e) => setDoorSplitPct(e.target.value)}
                affixEnd="%"
                aria-describedby={termsHintId}
              />
            </div>
            <p id={termsHintId} className={styles.termsHint}>
              Whichever is greater between the two values.
            </p>
          </div>
        ) : (
          <Field
            label="Amount (offer)"
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            affixStart="$"
          />
        )}

        <PillToggle legend="Type" options={KINDS} value={kind} onChange={setKind} />

        <Field
          label="Good through (optional)"
          type="date"
          value={goodThrough}
          onChange={(e) => setGoodThrough(e.target.value)}
          hint="When the offer lapses. Leave blank if they haven't said."
        />

        {/* The email, paraphrased. It is the one field here with no fixed shape, so it is LAST — the four
            above are what the board renders, and a tall box between them would push the deal off the fold.
            It stays with the offer: converting to a hold does not carry it (see `showCreateSeed.ts`). */}
        <Textarea
          label="Details (optional)"
          rows={4}
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          maxLength={OFFER_DETAILS_MAX}
          placeholder="Support, radius clause, backline, anything else they said."
          hint="Notes for the booking team. Not carried into the hold."
        />

        {/* What happens next (ART-326, design 4c). Not offered once a request is with the team, or passed. */}
        {!requested && !passed && (
          <fieldset className={styles.nextField}>
            <legend className={styles.nextLegend}>What happens next</legend>
            {([
              ["soft", "Soft hold", "Only your agency sees it. Nothing is sent yet."],
              ["team", "Send to team", "Next: fill in the hold editor, then it goes to Mgmt and the Artist AV."],
            ] as const).map(([value, label, sub]) => (
              <div key={value} className={styles.nextOption} data-on={next === value || undefined}>
                <input
                  id={`${nextId}-${value}`}
                  type="radio"
                  name="offer-next"
                  checked={next === value}
                  onChange={() => setNext(value)}
                  aria-describedby={`${nextId}-${value}-sub`}
                />
                <label htmlFor={`${nextId}-${value}`} className={styles.nextText}>
                  {label}
                  <span id={`${nextId}-${value}-sub`} className={styles.nextSub}>{sub}</span>
                </label>
              </div>
            ))}
          </fieldset>
        )}
      </Modal>

      {confirmingDelete && (
        <ConfirmDialog
          title="Delete this offer?"
          description="It comes off the pipeline board. Use Pass instead if you want to keep the record of turning it down."
          confirmLabel="Delete"
          loading={destroy.isPending}
          onConfirm={() =>
            destroy.mutate(offer!.offerId!, {
              onSuccess: () => {
                setConfirmingDelete(false);
                onClose();
              },
            })
          }
          onCancel={() => setConfirmingDelete(false)}
        />
      )}
    </>
  );
}
